import React, { useEffect, useState } from 'react';
import { trackFunnelEvent } from '../analytics';
import {
  Alert,
  Box,
  Button,
  Container,
  FormControl,
  FormControlLabel,
  Paper,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material';
import { ArrowForward, CheckCircle } from '@mui/icons-material';

const SKOOL_URL = 'https://www.skool.com/raymond/about';

type CommunitySegment =
  | ''
  | 'current_swe'
  | 'previously_swe_unemployed'
  | 'student_or_bootcamp'
  | 'career_switcher'
  | 'other';

const communityBenefits = [
  'A structured LeetCode roadmap',
  'Resume advice',
  'Interview preparation resources',
  'Weekly content and live coaching',
  'A community of engineers working toward top software engineering roles',
];

const JoinSkoolLandingPage: React.FC = () => {
  const [communitySegment, setCommunitySegment] = useState<CommunitySegment>('');
  const [error, setError] = useState('');
  const [isRedirecting, setIsRedirecting] = useState(false);

  useEffect(() => {
    document.title = 'Join Code With Raymond - Free Skool Community';
  }, []);

  const sendApplicationEvent = () => {
    if (window.fbq) {
      window.fbq('trackCustom', 'QualifiedEngineer', {
        content_name: 'Code With Raymond Free Skool Community',
        content_category: communitySegment,
        content_type: 'community_application',
      });
    }
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!communitySegment) {
      setError('Please answer the quick question before continuing.');
      return;
    }

    setError('');
    setIsRedirecting(true);
    sendApplicationEvent();
    trackFunnelEvent('skool_click', { destination: 'skool.com', page_path: window.location.pathname });

    window.setTimeout(() => {
      window.open(SKOOL_URL, '_self', 'noopener,noreferrer');
    }, 350);
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        overflow: 'hidden',
        background:
          'radial-gradient(circle at top left, rgba(235, 203, 139, 0.22), transparent 30%), linear-gradient(135deg, #101820 0%, #17242f 48%, #25313d 100%)',
        color: 'text.primary',
      }}
    >
      <Container maxWidth="md" sx={{ py: { xs: 4, md: 8 }, position: 'relative' }}>
        <Box
          sx={{
            position: 'absolute',
            top: 24,
            right: { xs: -140, md: -80 },
            width: 300,
            height: 300,
            borderRadius: '50%',
            background: 'rgba(136, 192, 208, 0.13)',
            filter: 'blur(4px)',
          }}
        />

        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, md: 5 },
            borderRadius: 5,
            border: '1px solid rgba(236, 239, 244, 0.16)',
            bgcolor: 'rgba(16, 24, 32, 0.82)',
            boxShadow: '0 24px 80px rgba(0, 0, 0, 0.35)',
            backdropFilter: 'blur(14px)',
          }}
        >
          <Typography
            variant="h1"
            sx={{
              color: '#F8FAFC',
              fontWeight: 950,
              fontSize: { xs: '2.45rem', md: '4.25rem' },
              lineHeight: 0.98,
              letterSpacing: '-0.055em',
              mt: 1,
              mb: 3,
            }}
          >
            Join 900+ Software Engineers Accelerating Their Careers
          </Typography>

          <Typography
            variant="h5"
            sx={{
              color: '#D8DEE9',
              lineHeight: 1.55,
              mb: 4,
            }}
          >
            Before I send you to the community, answer one quick question so we can personalize your
            experience.
          </Typography>

          <Box component="form" onSubmit={handleSubmit} noValidate>
            <FormControl sx={{ width: '100%' }}>
              <Typography sx={{ color: '#F8FAFC', fontWeight: 800, mb: 1 }}>
                Which best describes you right now?
              </Typography>
              <RadioGroup
                value={communitySegment}
                onChange={(event) => setCommunitySegment(event.target.value as CommunitySegment)}
              >
                <FormControlLabel
                  value="current_swe"
                  control={<Radio />}
                  label="I am currently employed as a software engineer"
                />
                <FormControlLabel
                  value="previously_swe_unemployed"
                  control={<Radio />}
                  label="I've worked as a software engineer, but now unemployed"
                />
                <FormControlLabel
                  value="student_or_bootcamp"
                  control={<Radio />}
                  label="I am a student or bootcamp learner"
                />
                <FormControlLabel
                  value="career_switcher"
                  control={<Radio />}
                  label="I am switching careers into software engineering"
                />
                <FormControlLabel
                  value="other"
                  control={<Radio />}
                  label="Something else"
                />
              </RadioGroup>
            </FormControl>

            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}

            {isRedirecting && (
              <Alert severity="success" sx={{ mt: 2 }}>
                Sending you to the free Code With Raymond community now.
              </Alert>
            )}

            <Button
              type="submit"
              variant="contained"
              size="large"
              fullWidth
              endIcon={<ArrowForward />}
              sx={{
                mt: 3,
                py: 1.7,
                color: '#101820',
                bgcolor: '#EBCB8B',
                fontWeight: 950,
                fontSize: '1.08rem',
                '&:hover': {
                  bgcolor: '#F0D79C',
                  transform: 'translateY(-1px)',
                },
              }}
            >
              Join the Free Community
            </Button>
          </Box>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            mt: 4,
            p: { xs: 3, md: 4 },
            borderRadius: 5,
            border: '1px solid rgba(235, 203, 139, 0.28)',
            bgcolor: 'rgba(236, 239, 244, 0.07)',
          }}
        >
          <Typography variant="h4" sx={{ color: '#F8FAFC', fontWeight: 950, mb: 2 }}>
            What happens next?
          </Typography>

          <Typography sx={{ color: '#D8DEE9', fontSize: '1.08rem', lineHeight: 1.7, mb: 2 }}>
            You'll immediately get access to the free Code With Raymond community, where you'll find:
          </Typography>

          <Box sx={{ display: 'grid', gap: 1.5, mb: 3 }}>
            {communityBenefits.map((benefit) => (
              <Box key={benefit} sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                <CheckCircle sx={{ color: '#A3BE8C', mt: 0.2 }} />
                <Typography sx={{ color: '#ECEFF4', fontSize: '1.04rem' }}>{benefit}</Typography>
              </Box>
            ))}
          </Box>

          <Typography sx={{ color: '#EBCB8B', fontWeight: 900, lineHeight: 1.6 }}>
            Everyone is welcome. Your answers simply help us personalize your experience and improve future
            training.
          </Typography>
        </Paper>
      </Container>
    </Box>
  );
};

export default JoinSkoolLandingPage;
